'use client';

import { useMemo, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { SectionHeader } from '../components/SectionHeader';
import { AdminDataTable } from '../components/AdminDataTable';
import { Button } from '@/core/presentation/components/ui/Button';
import { Input } from '@/core/presentation/components/ui/Input';
import { mockExpenses, type MockExpense, type MockExpenseType } from '../data/mockData';

type ExpenseFormState = {
  name: string;
  type: MockExpenseType;
  cost: string;
};

const INITIAL_FORM: ExpenseFormState = {
  name: '',
  type: 'operacional',
  cost: '',
};

const EXPENSE_TYPE_LABELS: Record<MockExpenseType, string> = {
  infraestrutura: 'Infraestrutura',
  marketing: 'Marketing',
  ferramentas: 'Ferramentas',
  operacional: 'Operacional',
  outros: 'Outros',
};

const EXPENSE_TYPE_OPTIONS: MockExpenseType[] = [
  'infraestrutura',
  'marketing',
  'ferramentas',
  'operacional',
  'outros',
];

function formatCurrencyBRL(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('pt-BR');
}

function createExpenseId(currentExpenses: MockExpense[]) {
  const maxNumber = currentExpenses.reduce((max, expense) => {
    const numericPart = Number(expense.id.replace('EXP-', ''));
    return Number.isFinite(numericPart) ? Math.max(max, numericPart) : max;
  }, 0);

  return `EXP-${String(maxNumber + 1).padStart(3, '0')}`;
}

export default function DespesasPage() {
  const [expenses, setExpenses] = useState<MockExpense[]>(mockExpenses);
  const [form, setForm] = useState<ExpenseFormState>(INITIAL_FORM);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);

  const isEditing = Boolean(editingExpenseId);

  const totalExpenses = useMemo(
    () => expenses.reduce((acc, expense) => acc + expense.cost, 0),
    [expenses]
  );

  const handleOpenCreateForm = () => {
    setEditingExpenseId(null);
    setForm(INITIAL_FORM);
    setIsFormOpen(true);
  };

  const handleStartEdit = (expense: MockExpense) => {
    setEditingExpenseId(expense.id);
    setForm({
      name: expense.name,
      type: expense.type,
      cost: String(expense.cost),
    });
    setIsFormOpen(true);
  };

  const handleDelete = (id: string) => {
    const shouldDelete = window.confirm('Deseja excluir esta despesa?');
    if (!shouldDelete) return;
    setExpenses((prev) => prev.filter((expense) => expense.id !== id));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const parsedCost = Number(form.cost.replace(',', '.'));
    if (!form.name.trim() || !Number.isFinite(parsedCost) || parsedCost <= 0) return;

    const nowIso = new Date().toISOString();

    if (isEditing && editingExpenseId) {
      setExpenses((prev) =>
        prev.map((expense) =>
          expense.id === editingExpenseId
            ? {
                ...expense,
                name: form.name.trim(),
                type: form.type,
                cost: parsedCost,
                updatedAt: nowIso,
              }
            : expense
        )
      );
    } else {
      setExpenses((prev) => [
        {
          id: createExpenseId(prev),
          name: form.name.trim(),
          type: form.type,
          cost: parsedCost,
          createdAt: nowIso,
          updatedAt: nowIso,
        },
        ...prev,
      ]);
    }

    setForm(INITIAL_FORM);
    setEditingExpenseId(null);
    setIsFormOpen(false);
  };

  const columns = [
    { key: 'id', header: 'ID' },
    { key: 'name', header: 'Nome' },
    {
      key: 'type',
      header: 'Tipo',
      render: (row: MockExpense) => EXPENSE_TYPE_LABELS[row.type],
    },
    {
      key: 'cost',
      header: 'Custo',
      render: (row: MockExpense) => formatCurrencyBRL(row.cost),
    },
    {
      key: 'createdAt',
      header: 'Criado em',
      render: (row: MockExpense) => formatDateTime(row.createdAt),
    },
    {
      key: 'updatedAt',
      header: 'Atualizado em',
      render: (row: MockExpense) => formatDateTime(row.updatedAt),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (row: MockExpense) => (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="border-admin-border text-admin-text hover:bg-admin-bg"
            onClick={() => handleStartEdit(row)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-admin-border text-destructive hover:bg-destructive/10"
            onClick={() => handleDelete(row.id)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <SectionHeader
        title="Despesas"
        subtitle={`Gerencie custos e acompanhe o total de despesas (${formatCurrencyBRL(totalExpenses)})`}
        actions={
          <Button className="bg-admin-navy hover:bg-admin-navy-mid text-white" onClick={handleOpenCreateForm}>
            <Plus className="h-4 w-4 mr-1" />
            Nova despesa
          </Button>
        }
      />

      {isFormOpen && (
        <form
          onSubmit={handleSubmit}
          className="mb-6 bg-white rounded-xl border border-admin-border p-4 shadow-sm grid grid-cols-1 md:grid-cols-4 gap-3"
        >
          <Input
            variant="light"
            label="Nome"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Ex.: Licença do sistema"
            fullWidth
            required
          />

          <div>
            <label className="block text-sm font-medium mb-1.5 text-navy-dark">Tipo</label>
            <select
              value={form.type}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, type: e.target.value as MockExpenseType }))
              }
              className="w-full rounded-lg px-4 py-3 text-text-body bg-[#fafbfc] border border-border-light focus:outline-none focus:border-cyan focus:ring-1 focus:ring-cyan transition-colors duration-200"
            >
              {EXPENSE_TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>
                  {EXPENSE_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </div>

          <Input
            variant="light"
            label="Custo (R$)"
            value={form.cost}
            onChange={(e) => setForm((prev) => ({ ...prev, cost: e.target.value }))}
            placeholder="0,00"
            fullWidth
            required
          />

          <div className="flex items-end gap-2">
            <Button
              type="submit"
              className="bg-admin-navy hover:bg-admin-navy-mid text-white w-full"
            >
              {isEditing ? 'Salvar alterações' : 'Adicionar'}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="border-admin-border text-admin-text hover:bg-admin-bg"
              onClick={() => {
                setIsFormOpen(false);
                setEditingExpenseId(null);
                setForm(INITIAL_FORM);
              }}
            >
              Cancelar
            </Button>
          </div>
        </form>
      )}

      <AdminDataTable
        data={expenses}
        columns={columns}
        searchKeys={['id', 'name', 'type']}
        emptyMessage="Nenhuma despesa cadastrada"
      />
    </div>
  );
}

